import { UseSuspenseQueryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  ImpactFactor,
  ImpactFactorRank,
  Journal,
  JournalAisMetric,
  JournalAisRank,
  JournalRequest,
} from '@/module/Journal';

import { Id, PagedParams, PagedResponse, getQueryKey } from '@feature/utils';

import * as api from './JournalApi';

const journalKey = getQueryKey('journal');

export function getJournalPagedOptions(params: PagedParams): UseSuspenseQueryOptions<PagedResponse<Journal>> {
  return {
    queryKey: journalKey.paged(params),
    queryFn: () => api.getPaged(params),
  };
}

export function getJournalOptions(journalId: Id): UseSuspenseQueryOptions<Journal> {
  return {
    queryKey: journalKey.one(journalId),
    queryFn: () => api.get(journalId),
  };
}

export function useJournalEdit(journalId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: JournalRequest) => api.edit(journalId, request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: journalKey.root(),
      }),
  });
}

export function useJournalCreate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: JournalRequest) => api.create(request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: journalKey.root(),
      }),
  });
}

export function useJournalDelete(journalId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.deleteById(journalId),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: journalKey.root(),
      }),
  });
}

export function getJournalImpactFactorMetricOptions(journalId: Id): UseSuspenseQueryOptions<ImpactFactor[]> {
  return {
    queryKey: journalKey.as([journalId, 'journalImpactFactor', 'metrics']),
    queryFn: () => api.getJournalImpactFactorMetric(journalId),
  };
}

export function getJournalImpactFactorRankOptions(journalId: Id): UseSuspenseQueryOptions<ImpactFactorRank[]> {
  return {
    queryKey: journalKey.as([journalId, 'journalImpactFactor', 'ranks']),
    queryFn: () => api.getJournalImpactFactorRank(journalId),
  };
}

export function getJournalAisMetricOptions(journalId: Id): UseSuspenseQueryOptions<JournalAisMetric[]> {
  return {
    queryKey: journalKey.as([journalId, 'journalAis', 'metrics']),
    queryFn: () => api.getJournalAisMetric(journalId),
  };
}

export function getJournalAisRankOptions(journalId: Id): UseSuspenseQueryOptions<JournalAisRank[]> {
  return {
    queryKey: journalKey.as([journalId, 'journalAis', 'ranks']),
    queryFn: () => api.getJournalAisRank(journalId),
  };
}
